import React, {FC} from 'react';

/** 退款说明英文正文(结构 1:1 对应 content/zh.tsx,类名复用 TermsOfUse__* BEM) */
const EnContent: FC = () => {
  return (
    <>
      <p className="v3-doc-meta">Effective Date: August 28, 2026</p>
      <p className="v3-doc-meta">Last Updated: August 28, 2026</p>
      <p>Thank you for using the products and services provided by Xiangji.</p>
      <p>
        This Xiangji Refund Policy (the “Policy”) applies to the subscription services, plans, credits, and other paid
        services you purchase from Xiangji.
      </p>
      <p>
        Xiangji aims to provide users with clear and transparent refund rules. Please read this Policy carefully and
        fully understand it before purchasing the relevant services, especially the provisions relating to refund
        eligibility, refund scope, refund exceptions, and subscription cancellation.
      </p>
      <p>
        If a specific Xiangji service you purchase has its own dedicated refund rules, those dedicated rules shall
        prevail.
      </p>
      <section>
        <h2>1. Scope of Refund Eligibility</h2>
        <h3>SaaS Subscription Services</h3>
        <p>
          For certain SaaS subscription services provided by Xiangji, Xiangji offers a{' '}
          <strong>30-day refund guarantee for the first purchase</strong>.
        </p>
        <p>
          Users who meet the refund conditions may submit a refund request to Xiangji within 30 calendar days from the
          date of the first purchase of the relevant subscription service.
        </p>
        <h3>Determination of First Purchase</h3>
        <p>
          Unless otherwise stated, “first purchase” means your first purchase of the corresponding SaaS subscription
          services with the same Xiangji account.
        </p>
        <p>
          <strong>
            If the same user repeatedly registers and purchases through different accounts, or circumvents the
            first-purchase restriction by other means, such purchases shall not be deemed a new first purchase.
          </strong>
        </p>
        <p>
          Xiangji has the right to determine whether a purchase qualifies as a first purchase based on the account,
          payment information, device information, order records, and other reasonable information.
        </p>
        <h3>Other Paid Services</h3>
        <p>
          Except for services explicitly marked as covered by the 30-day refund guarantee, whether other paid services
          support refunds shall be subject to the specific purchase page, order page, dedicated service agreement, or
          relevant rules.
        </p>
      </section>
      <section>
        <h2>2. Refund Conditions</h2>
        <p>You may request a refund if all of the following conditions are met:</p>
        <ol>
          <li>You are an eligible first-purchase user;</li>
          <li>
            You submit the refund request within 30 calendar days from the date of the first purchase of the relevant
            service;
          </li>
          <li>
            Your order is genuine and valid, and involves no fraud, malicious purchase, or other abnormal behavior;
          </li>
          <li>You have not violated the Xiangji Terms of Use or other relevant service rules;</li>
          <li>You meet the other refund conditions published on the specific service page.</li>
        </ol>
        <p>
          <strong>
            Refund eligibility does not mean that all purchased services, credits, or other benefits can be
            unconditionally refunded. The actual refund amount and refund scope shall be subject to this Policy and the
            specific service rules.
          </strong>
        </p>
      </section>
      <section>
        <h2>3. Refund Amount and Services Already Used</h2>
        <h3>Subscription Services</h3>
        <p>
          For subscription services that qualify for the 30-day refund guarantee, Xiangji will, in principle, refund the
          amount you actually paid for orders that meet the refund conditions.
        </p>
        <h3>Credits Already Used</h3>
        <p>
          If your subscription service also includes credits, AI generation credits, image translation credits, API call
          credits, or other quantifiable service benefits,
          <strong>
            Xiangji may make corresponding deductions from the refund amount based on the volume of services you have
            actually used before submitting the refund request.
          </strong>
        </p>
        <p>The specific deduction method shall be subject to the relevant service page or purchase rules.</p>
        <h3>Consumed Services</h3>
        <p>For services that have been actually used, consumed, or delivered and cannot be restored, for example:</p>
        <ul>
          <li>completed image translations;</li>
          <li>AI images already generated;</li>
          <li>image editing or generation services already used;</li>
          <li>video processing services already used;</li>
          <li>completed product detail page or marketing material generation;</li>
          <li>API calls already made;</li>
          <li>other services actually consumed;</li>
        </ul>
        <p>
          Xiangji has the right, based on actual usage, to deduct the corresponding charges upon refund or not to refund
          the consumed portion.
        </p>
        <h3>Third-Party Fees</h3>
        <p>
          <strong>
            If the relevant services involve fees incurred by third-party platforms, AI models, payment institutions,
            cloud service providers, or other third parties, and such fees have actually been incurred and are
            non-refundable, Xiangji has the right to deduct the corresponding fees from the refund amount.
          </strong>
        </p>
      </section>
      <section>
        <h2>4. Circumstances Where Refunds Are Not Applicable</h2>
        <p>In the following circumstances, Xiangji in principle does not provide refunds:</p>
        <ol>
          <li>Refund requests submitted more than 30 calendar days after the first purchase;</li>
          <li>Non-first purchases, including upgrades, renewals, repurchases, or other repeat purchases;</li>
          <li>Specific services or products explicitly marked as “non-refundable”;</li>
          <li>Credits or services already used or consumed;</li>
          <li>Fees arising from third-party services that cannot be recovered by Xiangji;</li>
          <li>
            Purchase errors caused by your own reasons, such as purchasing the wrong plan, account, quantity, or
            service;
          </li>
          <li>You violate the Xiangji Terms of Use or other relevant rules;</li>
          <li>
            Malicious registration, bulk registration, abnormal purchases, credit farming, abuse of the refund policy,
            or similar behavior;
          </li>
          <li>Fraud, fake transactions, unauthorized card charges, or other abnormal payment behavior;</li>
          <li>
            Other circumstances where refunds are precluded by laws and regulations or are inappropriate given the
            nature of the specific service.
          </li>
        </ol>
        <p>
          <strong>
            If Xiangji has reasonable grounds to believe that a user has engaged in malicious refunds, repeated refunds,
            or other abuse of the refund policy, Xiangji has the right to reject the relevant refund request and take
            corresponding measures in accordance with the Terms of Use.
          </strong>
        </p>
      </section>
      <section>
        <h2>5. Subscription Cancellation and Auto-Renewal</h2>
        <h3>Subscription Cancellation</h3>
        <p>
          If you do not wish to continue using Xiangji subscription services, you may cancel the subscription using the
          method provided on the product page.
        </p>
        <p>
          <strong>Cancelling a subscription does not automatically result in a refund.</strong>
        </p>
        <p>
          If you have already paid for the current subscription period, cancellation usually only means turning off
          subsequent auto-renewal, and you may continue to use the corresponding services within the current paid
          service period unless the specific service rules provide otherwise.
        </p>
        <h3>Auto-Renewal</h3>
        <p>
          For subscription services that support auto-renewal, you may turn off auto-renewal following the relevant
          method on the subscription page or in your account settings.
        </p>
        <p>
          After auto-renewal is turned off, the currently paid subscription period will usually not be automatically
          refunded as a result.
        </p>
        <h3>Renewal Orders</h3>
        <p>
          <strong>
            Except as otherwise provided by laws and regulations or explicitly stated on the specific service page,
            orders arising from subscription renewals, auto-renewals, and upgrades are not covered by the 30-day
            first-purchase refund guarantee.
          </strong>
        </p>
      </section>
      <section>
        <h2>6. Refund Request Process</h2>
        <p>
          If you meet the refund conditions, you may submit a refund request to Xiangji through the following channels:
        </p>
        <p>
          <strong>Customer service email: </strong>
          <a href="mailto:bussiness@tosoiot.com">bussiness@tosoiot.com</a>
        </p>
        <p>
          <strong>Contact phone: </strong>+86 176 6407 5341
        </p>
        <p>When requesting a refund, please provide as much of the following as possible:</p>
        <ul>
          <li>Your Xiangji account;</li>
          <li>Order number;</li>
          <li>Purchase time;</li>
          <li>The product or subscription plan purchased;</li>
          <li>Reason for the refund;</li>
          <li>Other necessary information related to the order.</li>
        </ul>
        <p>
          To verify the order and protect account security, Xiangji may ask you to provide necessary order and identity
          verification information.
        </p>
      </section>
      <section>
        <h2>7. Refund Review and Settlement</h2>
        <h3>Review Time</h3>
        <p>
          Xiangji will review your request after receiving a complete refund application and will usually provide the
          review result within <strong>3 business days</strong>.
        </p>
        <p>
          If the refund request involves complex orders, abnormal transactions, third-party payment institutions, or
          other situations requiring further verification, the review time may be extended accordingly.
        </p>
        <h3>Refund Method</h3>
        <p>
          <strong>
            In principle, refunds will be returned via the original payment method to the payment account or payment
            method you actually used when paying for the order.
          </strong>
        </p>
        <p>
          If a refund via the original payment method is impossible due to the payment institution, bank, account
          status, or other objective reasons, Xiangji will discuss with you other reasonable refund methods.
        </p>
        <h3>Settlement Time</h3>
        <p>
          After the refund is approved by Xiangji, the specific time of crediting depends on the processing time of the
          relevant payment institutions, banks, and other financial service institutions.
        </p>
        <p>
          <strong>
            Xiangji cannot guarantee that a refund will be credited within a specific number of business days.
          </strong>
        </p>
        <p>
          If the refund has been marked as completed but you still have not received the funds within a reasonable
          period, please contact Xiangji customer service in time, and we will help you check the refund status.
        </p>
      </section>
      <section>
        <h2>8. Service Adjustments After Refund</h2>
        <p>
          After a refund is completed, Xiangji has the right to make corresponding adjustments to your account and
          related benefits according to the type of service involved in the refund, including but not limited to:
        </p>
        <ul>
          <li>Cancelling or terminating the relevant subscription services;</li>
          <li>Deducting or clearing unused credits related to the refunded order;</li>
          <li>Cancelling related membership benefits;</li>
          <li>Revoking unused credits or service counts;</li>
          <li>Restricting continued use of the refunded services.</li>
        </ul>
        <p>
          <strong>
            If, after the refund is completed, the service benefits corresponding to the relevant order continue to be
            used, Xiangji has the right to recalculate the relevant charges based on actual usage or take other
            reasonable measures.
          </strong>
        </p>
      </section>
      <section>
        <h2>9. Special Payments and Third-Party Platforms</h2>
        <p>
          If you purchase Xiangji services through Apple, Google, PayPal, Stripe, or other third-party payment
          platforms, the refund may need to be processed in accordance with the rules of the relevant payment platform.
        </p>
        <p>
          <strong>
            If the third-party platform has special provisions on refund requests, refund timing, or refund methods, you
            may need to submit the request following the process of the relevant platform.
          </strong>
        </p>
        <p>
          For handling fees, exchange rate differences, payment failures, refund delays, or other issues arising from
          the third-party payment platform itself, Xiangji will assist in handling them within a reasonable scope, but
          assumes no liability for losses beyond the control of Xiangji.
        </p>
      </section>
      <section>
        <h2>10. Modification of the Refund Policy</h2>
        <p>
          <strong>
            Xiangji has the right to modify this Refund Policy in response to changes in laws and regulations, business
            adjustments, changes in payment methods, and actual operating conditions.
          </strong>
        </p>
        <p>
          If the Refund Policy changes materially, Xiangji will notify you through website announcements, product pages,
          pop-ups, in-site notifications, or other reasonable means.
        </p>
        <p>
          <strong>
            For services you have already purchased and that are still within their validity period, matters involving
            refund rights shall in principle be governed by the Refund Policy applicable at the time of your purchase,
            except as otherwise provided by laws and regulations or agreed by both parties.
          </strong>
        </p>
        <p>The modified Refund Policy takes effect on the date of publication or the specified effective date.</p>
      </section>
      <section>
        <h2>11. Contact Us</h2>
        <p>
          If you have any questions about the Refund Policy, refund eligibility, refund amounts, or the refund process,
          you may contact us through the following channels:
        </p>
        <ul>
          <li>
            <strong>Customer service email: </strong>
            <a href="mailto:bussiness@tosoiot.com">bussiness@tosoiot.com</a>
          </li>
          <li>
            <strong>Contact phone: </strong>+86 176 6407 5341
          </li>
          <li>
            <strong>Official website: </strong>
            <a href="https://www.xiangjifanyi.com/" rel="noreferrer" target="_blank">
              https://www.xiangjifanyi.com/
            </a>
          </li>
        </ul>
        <p>We will process your refund request or inquiry within a reasonable period after receiving it.</p>
        <p>
          <strong>Special note: </strong>
        </p>
        <p>
          The Refund Policy does not affect other statutory rights you are entitled to under applicable laws and
          regulations. If this Policy conflicts with applicable laws and regulations, the mandatory provisions of the
          laws and regulations shall prevail.
        </p>
      </section>
    </>
  );
};

export default EnContent;
